import { BreakpointObserver } from '@angular/cdk/layout';
import { Platform } from '@angular/cdk/platform';
import { CommonModule, PlatformLocation } from '@angular/common';
import { Component, Inject, NgModule, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router, RouterModule, ActivationEnd } from '@angular/router';
import { WINDOW } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { AlertTemplatesModule } from '@app/components/alert-templates/alert-templates.component';
import { BroadcastsModule } from '@app/components/broadcasts/broadcasts/broadcasts.component';
import { FirstDayDialogComponent, FirstDayDialogModule } from '@app/components/first-day-dialog/first-day-dialog.component';
import { FooterModule } from '@app/components/footer/footer.component';
import { TopnavBroadcastsModule } from '@app/components/header/topnav-broadcasts/topnav-broadcasts.component';
import { TopnavModule } from '@app/components/header/topnav/topnav.component';
import { IosPwaBannerComponent, IosPwaBannerModule } from '@app/components/ios-pwa-banner/ios-pwa-banner.component';
import { SkipLinksModule } from '@app/components/skip-links/skip-links.component';
import { StickyFillModule } from '@app/directives/stickyfill.directive';
import { ComponentState } from '@app/models/component-state';
import { FirstDayWizardStep } from '@app/models/first-day-step';
import { InternetStatus } from '@app/models/internet-status';
import { LinkModule } from '@app/directives/link.directive';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { InternetStatusService } from '@app/services/internet-status.service';
import { SignalRService } from '@app/services/signalr.service';
import { UpdatedContentService } from '@app/services/updated-content.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { UserService } from '@app/services/user.service';
import { Observable, Subscription } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { Utilities } from '@app/common/utilities';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class MainComponent implements OnInit, OnDestroy {

  subscriptions = new Subscription();
  firstDayDialogRef: MatDialogRef<FirstDayDialogComponent>;
  @ViewChild('sidenav') sidenav: MatSidenav;
  stickyHeader = this.featureSettingsStoreService.state.stickyHeader;
  internetStatus$: Observable<InternetStatus>;

  constructor(
    @Inject(WINDOW) private window: Window,
    private titleService: Title,
    private userService: UserService,
    private componentStateService: ComponentStatesOnRoutesService,
    private signalRService: SignalRService,
    private userChannelService: UserChannelTermsService,
    private userLanguageTermService: UserLanguageTermService,
    private platformLocation: PlatformLocation,
    private featureSettingsStoreService: FeatureSettingsStoreService,
    private dialog: MatDialog,
    private vcr: ViewContainerRef,
    private updatedContentService: UpdatedContentService,
    private internetStatusService: InternetStatusService,
    private router: Router,
    private platform: Platform,
    private breakpointObserver: BreakpointObserver) {
    this.componentStateService.initStates(this.vcr);
  }

  toggleSideMenu(open: boolean, sidenav: MatSidenav) {
    sidenav.toggle(open);
  }

  // Fullscreen means no toolbar or footer, just a single outlet for the config component
  fullscreenView() {
    return this.router.url.indexOf('first-day') >= 0 || this.featureSettingsStoreService.state.coreIntranetEnabled;
  }

  reload() {
    this.window.location.reload();
  }

  ngOnInit(): void {
    this.internetStatus$ = this.internetStatusService.state$.pipe(map(status => status));
    this.subscriptions.add(this.internetStatusService.whenOnline().subscribe());
    this.subscriptions.add(this.internetStatusService.whenOffline().subscribe());

    this.subscriptions.add(
      this.router.events
        .pipe(filter(event => event instanceof NavigationStart))
        .subscribe((event: NavigationStart) => {
          // Close sidenav when navigating
          if (event && this.sidenav && this.sidenav.opened) {
            this.toggleSideMenu(false, this.sidenav);
          }
        })
    );

    this.subscriptions.add(
      this.router.events
        .pipe(
          filter(event => event instanceof ActivationEnd)
        )
        .subscribe((event: ActivationEnd) => {
          if (event.snapshot.data?.title) {
            this.setTitle(event.snapshot.data?.title);
          }
        })
    );

    if (this.featureSettingsStoreService.state.enableUpdateChecksForNewContent) {
      this.subscriptions.add(
        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd),
          mergeMap((event: NavigationEnd) => {
            return this.updatedContentService.onNavigationEnd(event);
          }))
          .subscribe(() => {
            console.log('checked');
          })
      );
    }

    if (this.featureSettingsStoreService.state.enableFirstDayPopupDialog) {
      this.subscriptions.add(
        this.userService
          .getOrCreateMe()
          .subscribe(user => {
            if (user.FirstDayWizardStep === FirstDayWizardStep.None) {
              if (!this.firstDayDialogRef) {
                this.firstDayDialogRef = this.dialog.open(FirstDayDialogComponent, {
                  minWidth: '50vw',
                  panelClass: 'first-day-dialog-container',
                  autoFocus: true,
                  disableClose: true,
                  closeOnNavigation: false,
                });
              }
            }
          })
      );
    }

    if (this.featureSettingsStoreService.state.enableSignalR) {
      this.subscriptions.add(
        this.signalRService
          .connect()
          .subscribe((connection) => {
            connection.on('UpdateComponentState', (componentState: ComponentState) => {
              if (componentState) {
                this.componentStateService.updateStates(new ComponentState(componentState));
              }
            });
          })
      );
    }

    this.subscriptions.add(
      this.userChannelService
        .syncUserChannels(this.platformLocation.pathname)
        .subscribe()
    );

    if (this.featureSettingsStoreService.state.enableAutoUserChannelSync) {
      this.subscriptions.add(
        this.userChannelService
          .syncUserChannelsWithAD(this.platformLocation.pathname)
          .subscribe()
      );
    }

    if (this.featureSettingsStoreService.state.enableAutoUserLanguageSync) {
      this.subscriptions.add(
        this.userLanguageTermService
          .syncUserLanguageTermsWithAD(this.platformLocation.pathname)
          .subscribe()
      );
    }

    if (this.featureSettingsStoreService.state.enableIosA2hsBanner && this.platform.IOS && Utilities.isSafari(this.window) && !this.isInStandAloneMode() && !this.hasUserClosedBanner()) {
      this.dialog.open(IosPwaBannerComponent, {
        minWidth: '80vw',
        panelClass: 'ios-banner-day-dialog-container',
        closeOnNavigation: true,
        hasBackdrop: false,
        position: { bottom: '1rem' }
      });
    }

  }

  private setTitle(title: string) {
    if (title?.length > 0) {
      this.titleService.setTitle(`${this.featureSettingsStoreService.state.title} - ${title}`);
    } else {
      this.titleService.setTitle(this.featureSettingsStoreService.state.title);
    }
  }
  private isInStandAloneMode() {
    return ('standalone' in this.window.navigator && this.window.navigator['standalone'] === true) || this.breakpointObserver.isMatched('(display-mode: standalone)');
  }

  // Check if user has closed ios banner previously, use cookie because of simplicity and our cache gets clered on every application update.
  private hasUserClosedBanner() {
    return document.cookie.split(';').some(item => item.indexOf(Constants.IOS_BANNER_CLOSED_COOKIE) >= 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
@NgModule({
  declarations: [
    MainComponent,
  ],
  exports: [MainComponent],
  imports: [
    CommonModule,
    RouterModule,
    BroadcastsModule,
    MatSidenavModule,
    FooterModule,
    TopnavModule,
    MatButtonModule,
    TopnavBroadcastsModule,
    MatProgressSpinnerModule,
    LinkModule,
    FirstDayDialogModule,
    AlertTemplatesModule,
    StickyFillModule,
    IosPwaBannerModule,
    SkipLinksModule
  ]
})
export class MainModule { }
